import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { loadCatalogue } from "@/components/procurement/ItemPicker";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT", "STORES"];
const CATEGORIES = { STOCK: "Stock (kept in a warehouse)", NON_STOCK: "Non-stock (used directly)", SERVICE: "Service / work" };
const EMPTY = { code: "", name: "", description: "", category: "STOCK", uom_id: "", standard_rate: "", hsn_code: "", reorder_level: "", is_active: true };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Items() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, u] = await Promise.all([api.get("/inventory/items"), api.get("/masters/uom").catch(() => ({ data: [] }))]);
      setItems(data);
      setUoms(u.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const uomName = (id) => { const u = uoms.find((x) => x.id === id); return u ? u.code || u.name : "—"; };
  const [suggested, setSuggested] = useState("");
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  useEffect(() => {
    if (!open || editing) return;
    api.get("/codes/next", { params: { kind: "item", category: form.category } }).then(({ data }) => setSuggested(data.code)).catch(() => setSuggested(""));
  }, [open, editing, form.category]);
  const openEdit = (i) => {
    setEditing(i);
    setForm({
      code: i.code, name: i.name, description: i.description || "", category: i.category, uom_id: i.uom_id || "",
      standard_rate: i.standard_rate ? String(Number(i.standard_rate)) : "", hsn_code: i.hsn_code || "",
      reorder_level: i.reorder_level ? String(Number(i.reorder_level)) : "", is_active: i.is_active,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description || null, category: form.category, uom_id: form.uom_id || null,
        standard_rate: form.standard_rate || "0", hsn_code: form.hsn_code || null, reorder_level: form.reorder_level || null, is_active: form.is_active,
      };
      if (editing) await api.patch(`/inventory/items/${editing.id}`, payload);
      else await api.post("/inventory/items", { ...payload, code: form.code.trim() || null });
      toast.success(editing ? "Item updated — new price is used from now on" : "Item added to the catalogue");
      loadCatalogue(true);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/inventory/items/${item.id}`, { is_active: !item.is_active });
      loadCatalogue(true);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const q = query.toLowerCase().trim();
  const shown = items.filter((i) => (category === "ALL" || i.category === category)
    && (!q || [i.code, i.name, i.description, i.hsn_code].some((t) => String(t || "").toLowerCase().includes(q))));

  return (
    <div className="space-y-6" data-testid="items-page">
      <PageHeader
        title="Items & Prices"
        description="Your catalogue of materials and services with the price for 1 unit. Every procurement form (indent, requisition, RFQ, PO, work order) offers these in a dropdown and fills the price — which you can still change on the document."
        action={canWrite && <Button onClick={openNew} data-testid="create-item-button"><Plus className="h-4 w-4" /> New Item</Button>}
      />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search code, name, description, HSN" className="pl-8" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All categories</SelectItem>
            {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{shown.length} item(s)</span>
      </div>
      <div className="rounded-xl border border-border bg-card">
        {!loading && items.length === 0 ? (
          <EmptyState title="No items yet" description="Add materials (cement, modules, cable…) and services (piling per pile, crane per hour…) with their price." testId="items-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Item</TableHead><TableHead>Category</TableHead><TableHead>Unit</TableHead>
              <TableHead className="text-right">Price / unit</TableHead><TableHead>HSN / SAC</TableHead><TableHead className="text-right">Reorder level</TableHead><TableHead>Status</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {shown.map((i) => (
                <TableRow key={i.id} data-testid={`item-row-${i.code}`}>
                  <TableCell className="font-mono text-xs">{i.code}</TableCell>
                  <TableCell className="max-w-xs"><div className="text-foreground">{i.name}</div>{i.description && <div className="truncate text-xs text-muted-foreground">{i.description}</div>}</TableCell>
                  <TableCell className="text-xs">{i.category === "SERVICE" ? "Service" : i.category === "NON_STOCK" ? "Non-stock" : "Stock"}</TableCell>
                  <TableCell className="text-xs">{uomName(i.uom_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{Number(i.standard_rate) > 0 ? inr(i.standard_rate) : <span className="font-sans text-amber-600 dark:text-amber-400">not set</span>}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{i.hsn_code || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{i.reorder_level ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={i.is_active} disabled={!canWrite} onCheckedChange={() => toggleActive(i)} />
                      <Badge variant={i.is_active ? "default" : "secondary"}>{i.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{canWrite && <Button size="sm" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-item-${i.code}`}><Pencil className="h-3.5 w-3.5" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.code}` : "New Item"}</DialogTitle>
            <DialogDescription>Changing the price affects new documents only — existing POs keep their own price.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3.5" data-testid="item-form">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Code {!editing && <span className="font-normal text-muted-foreground">(auto)</span>}</Label><Input disabled={Boolean(editing)} value={form.code} onChange={set("code")} placeholder={suggested || "Auto"} className="mt-1.5" data-testid="item-code-input" />{!editing && <p className="mt-0.5 text-[10px] text-muted-foreground">Blank = {suggested || "next number"}</p>}</div>
              <div className="col-span-2"><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="item-name-input" placeholder="e.g. Cement OPC 53 grade" /></div>
            </div>
            <div><Label>Description <span className="font-normal text-muted-foreground">(specification, brand, size)</span></Label><Textarea value={form.description} onChange={set("description")} className="mt-1.5" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={form.uom_id || "none"} onValueChange={(v) => setForm({ ...form, uom_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {uoms.map((u) => <SelectItem key={u.id} value={u.id}>{u.code}{u.name && u.name !== u.code ? ` — ${u.name}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Price for 1 unit (₹, before GST)</Label><Input type="number" step="0.01" min="0" value={form.standard_rate} onChange={set("standard_rate")} className="mt-1.5" data-testid="item-rate-input" /></div>
              <div><Label>HSN / SAC code</Label><Input value={form.hsn_code} onChange={set("hsn_code")} className="mt-1.5" /></div>
              {form.category === "STOCK" && <div><Label>Reorder level</Label><Input type="number" step="0.001" min="0" value={form.reorder_level} onChange={set("reorder_level")} className="mt-1.5" /></div>}
            </div>
            <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
